import React from 'react';
import { ButtonBase } from '@mui/material';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';

export interface DisclosureLinkProps {
  children: React.ReactNode;
  /** Whether what it opens is open; the chevron turns down. */
  open: boolean;
  onClick: () => void;
  'aria-label'?: string;
  'aria-controls'?: string;
}

/**
 * A small text link that opens something below it — "Agent breakdown ›"
 * under a figure. Quieter than a button, for a detail most readers pass by;
 * the chevron says it opens rather than navigates.
 */
export function DisclosureLink({ children, open, onClick, ...aria }: DisclosureLinkProps) {
  return (
    <ButtonBase
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-expanded={open}
      {...aria}
      sx={(t) => ({
        ...t.typography.caption,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        whiteSpace: 'nowrap',
        color: open ? t.tokens.brand.primary : t.tokens.text.tertiary,
        borderRadius: `${t.ds.radius.xs}px`,
        '&:hover': { color: t.tokens.brand.primary },
        '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
        '& svg': {
          fontSize: t.typography.caption.fontSize,
          transform: open ? 'rotate(90deg)' : 'none',
          transition: `transform ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        },
      })}
    >
      {children}
      <ChevronRightRounded aria-hidden />
    </ButtonBase>
  );
}
